'use client';

import { canAccessSchool } from '@/core/auth/access';
import { EntitlementType, SpikemarkUser, TeamSessionData } from '@spikemark/auth';
import { PackageInfo } from '@spikemark/onboarding/payments-service/types';
import { SchoolTeamGenderEnum } from '@spikemark/rest-api';
import { redirect, useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createContext, FC, useCallback, useContext, useMemo } from 'react';
type SelectProps = {
  label: string;
  value: string;
  title?: string;
};
type UserContextType = {
  activeTeamId: string;
  activeTeam: TeamSessionData | undefined;
  mensTeam?: TeamSessionData;
  womensTeam?: TeamSessionData;
  teamGenderOptions: SelectProps[];
  seasonOptions: SelectProps[];
  requestAnalyticsTeamChange: (teamId: string) => void;
  requestAnalyticsSeasonChange: (teamId: string) => void;
  defaultSeason: string;
  activeSeason: string;
  activeTeamPlan: EntitlementType | null;
  userTeams: TeamSessionData[];
  tierData: {
    proPackage?: PackageInfo;
    standardPackage?: PackageInfo;
  };
};
const UserContext = createContext<UserContextType>({
  activeTeamId: '',
  activeTeam: undefined,
  userTeams: [],
  teamGenderOptions: [],
  requestAnalyticsTeamChange: () => {},
  requestAnalyticsSeasonChange: () => {},
  seasonOptions: [],
  defaultSeason: '',
  activeSeason: '',
  activeTeamPlan: null,
  tierData: {
    proPackage: undefined,
    standardPackage: undefined
  }
});
const DEFAULT_ACTIVE_SEASON = '2025';
export const SEASON_OPTIONS = [{
  label: 'Season 2025/26',
  value: '2026'
}, {
  label: 'Season 2024/25',
  value: '2025'
}, {
  label: 'Season 2023/24',
  value: '2024'
}];
export const UserProvider: FC<{
  defaultSeason?: string;
  user: SpikemarkUser;
  isAdmin?: boolean;
  tierData: {
    proPackage: PackageInfo;
    standardPackage: PackageInfo;
  };
  children: React.ReactNode;
}> = ({
  user,
  isAdmin,
  children,
  tierData,
  defaultSeason = DEFAULT_ACTIVE_SEASON
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const {
    teamId
  } = useParams();
  const searchParams = useSearchParams();
  const activeSeason = searchParams.get('season') ?? defaultSeason;
  const {
    userTeams,
    activeTeam,
    activeTeamId,
    activeTeamPlan
  } = useMemo(() => {
    const schools = user?.schools || {};
    const userTeams = Object.values(schools).filter(s => s.schoolId);
    const activeTeamId: string = teamId as string ?? userTeams[0]?.schoolId;
    const activeTeam = userTeams.find(s => s.schoolId === activeTeamId);
    let activeTeamPlan: EntitlementType = EntitlementType.CoachPortalAccess;
    if (canAccessSchool(schools, activeTeamId, EntitlementType.ProAnalytics)) {
      activeTeamPlan = EntitlementType.ProAnalytics;
    } else if (canAccessSchool(schools, activeTeamId, EntitlementType.QualifyingLiveScoring)) {
      activeTeamPlan = EntitlementType.QualifyingLiveScoring;
    }
    return {
      userTeams,
      activeTeamId,
      activeTeam,
      activeTeamPlan
    };
  }, [teamId, user?.schools]);
  const {
    mensTeam,
    womensTeam,
    teamGenderOptions
  } = useMemo(() => {
    let mensTeam: TeamSessionData | undefined;
    let womensTeam: TeamSessionData | undefined;
    userTeams.forEach(team => {
      if (team.gender === SchoolTeamGenderEnum.Men) {
        mensTeam = team;
      } else if (team.gender === SchoolTeamGenderEnum.Women) {
        womensTeam = team;
      }
    });
    const teamGenderOptions: {
      label: string;
      value: string;
      title?: string;
    }[] = [];
    if (mensTeam) {
      teamGenderOptions.push({
        label: 'Men’s',
        value: mensTeam.schoolId,
        title: `${mensTeam.gender}'s team`
      });
    }
    if (womensTeam) {
      teamGenderOptions.push({
        label: 'Women’s',
        value: womensTeam.schoolId,
        title: `${womensTeam.gender}'s team`
      });
    }
    if (isAdmin) {
      userTeams.forEach(team => {
        if (![mensTeam?.schoolId, womensTeam?.schoolId].includes(team.schoolId)) {
          teamGenderOptions.push({
            label: team.gender,
            value: team.schoolId,
            title: `${team.gender}'s team`
          });
        }
      });
    }
    return {
      mensTeam,
      womensTeam,
      teamGenderOptions
    };
  }, [isAdmin, userTeams]);
  const handleAnalyticsSeasonChange = useCallback((season: string, defaultSeason: string) => {
    if (!SEASON_OPTIONS.some(s => s.value === season)) return;
    const newParams = new URLSearchParams(searchParams.toString());
    if (season === defaultSeason) {
      newParams.delete('season');
    } else {
      newParams.set('season', season);
    }
    router.replace(`${pathname}?${newParams.toString()}`);
  }, [pathname, router, searchParams]);
  const requestAnalyticsTeamChange = useCallback((nextTeamId: string) => {
    if (!userTeams.some(s => s.schoolId === nextTeamId)) return;
    const [, path] = pathname.split(`${teamId}/`);
    router.replace(`/analytics/${nextTeamId}/${path}`);
  }, [pathname, router, searchParams, userTeams]);
  return <UserContext.Provider value={{
    activeTeam,
    activeTeamId,
    userTeams,
    mensTeam,
    womensTeam,
    teamGenderOptions,
    requestAnalyticsTeamChange,
    requestAnalyticsSeasonChange: (season: string) => handleAnalyticsSeasonChange(season, defaultSeason),
    seasonOptions: SEASON_OPTIONS,
    defaultSeason,
    activeSeason,
    activeTeamPlan,
    tierData
  }} data-sentry-element="unknown" data-sentry-component="UserProvider" data-sentry-source-file="user-context-provider.tsx">
      {children}
    </UserContext.Provider>;
};
export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};